import { useState, useEffect, useCallback, useMemo } from 'react'
import { useAuth } from '../context/AuthContext.js'
import './Documents.css'

const today = () => new Date().toISOString().split('T')[0]

export default function Documents() {
  const { apiFetch, admin, isOwner } = useAuth()

  const [tenants,   setTenants]   = useState([])
  const [tenantId,  setTenantId]  = useState(isOwner ? null : admin?.tenant_id)
  const [dateMode,  setDateMode]  = useState('day')   // 'day' | 'range' | 'all'
  const [date,      setDate]      = useState(today)
  const [dateFrom,  setDateFrom]  = useState(today)
  const [dateTo,    setDateTo]    = useState(today)
  const [allRows,   setAllRows]   = useState([])
  const [loading,   setLoading]   = useState(false)
  const [lightbox,   setLightbox]  = useState(null)   // { url, type: 'image'|'pdf' }
  const [lbLoading,  setLbLoading] = useState(null)   // sessionId currently loading
  const [filterText, setFilterText] = useState('')
  const [sortKey,    setSortKey]   = useState('visit_date')
  const [sortDir,    setSortDir]   = useState('desc')

  useEffect(() => {
    if (!isOwner) return
    apiFetch('/api/admin/tenants')
      .then(r => r.json())
      .then(data => {
        if (Array.isArray(data) && data.length > 0) {
          setTenants(data)
          setTenantId(data[0].id)
        }
      })
      .catch(() => {})
  }, [isOwner])

  const load = useCallback(() => {
    if (!tenantId) return
    setLoading(true)
    let qs = ''
    if (dateMode === 'day'   && date)              qs = `?date=${date}`
    if (dateMode === 'range' && dateFrom && dateTo) qs = `?from=${dateFrom}&to=${dateTo}`
    apiFetch(`/api/admin/checkins/${tenantId}${qs}`)
      .then(r => r.json())
      .then(data => setAllRows(Array.isArray(data) ? data : []))
      .catch(() => setAllRows([]))
      .finally(() => setLoading(false))
  }, [tenantId, dateMode, date, dateFrom, dateTo])

  useEffect(() => { load() }, [load])

  function handleSort(key) {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    else { setSortKey(key); setSortDir('asc') }
  }

  // Only show rows that have at least one file attached
  const baseRows = allRows.filter(r => r.document_available || r.pdf_available)

  const rows = useMemo(() => {
    let result = [...baseRows]
    if (filterText) {
      const q = filterText.toLowerCase()
      result = result.filter(r =>
        `${r.first_name} ${r.last_name}`.toLowerCase().includes(q) ||
        (r.company || '').toLowerCase().includes(q) ||
        (r.flow_mode || '').toLowerCase().includes(q)
      )
    }
    result.sort((a, b) => {
      let va, vb
      switch (sortKey) {
        case 'name':     va = `${a.first_name} ${a.last_name}`; vb = `${b.first_name} ${b.last_name}`; break
        case 'company':  va = a.company   || ''; vb = b.company   || ''; break
        case 'type':     va = a.flow_mode || ''; vb = b.flow_mode || ''; break
        default:         va = a.visit_date || ''; vb = b.visit_date || ''
      }
      const cmp = va.localeCompare(vb, 'de')
      return sortDir === 'asc' ? cmp : -cmp
    })
    return result
  }, [baseRows, filterText, sortKey, sortDir])

  const openFile = async (sessionId, apiType, visitorName, visitDate) => {
    // apiType: 'pdf' = safety-rules PDF, 'image' = document scan (may be jpg or pdf)
    setLbLoading(sessionId + apiType)
    try {
      const path = apiType === 'pdf'
        ? `/api/admin/checkins/${sessionId}/pdf`
        : `/api/admin/checkins/${sessionId}/document`
      const res  = await apiFetch(path)
      const blob = await res.blob()
      const url  = URL.createObjectURL(blob)

      if (apiType === 'pdf') {
        // Safety-rules PDF — download directly instead of opening a new tab.
        const a = document.createElement('a')
        a.href = url
        a.download = `Sicherheitsregeln-${visitDate}-${(visitorName || '').replace(/\s+/g, '-')}.pdf`
        a.click()
        URL.revokeObjectURL(url)
      } else if (blob.type === 'application/pdf') {
        // Use actual Content-Type — document scans are now PDFs, not always JPEG
        window.open(url, '_blank')
        setTimeout(() => URL.revokeObjectURL(url), 10_000)
      } else {
        setLightbox({ url, type: 'image' })
      }
    } catch {
      alert('Datei konnte nicht geladen werden.')
    } finally {
      setLbLoading(null)
    }
  }

  const closeLightbox = () => {
    if (lightbox?.url) URL.revokeObjectURL(lightbox.url)
    setLightbox(null)
  }

  const formatDate = (d) => d ? new Date(d + 'T12:00:00').toLocaleDateString('de-DE') : '—'

  return (
    <div>
      <div className="page-header">
        <h1>Dokumente</h1>
        <p>Ausweisscans und Sicherheitsregel-PDFs aller Besucher.</p>
      </div>

      {/* Filters */}
      <div className="card doc-filters">
        {isOwner && tenants.length > 0 && (
          <select
            className="form-control doc-filter-field"
            value={tenantId || ''}
            onChange={e => setTenantId(e.target.value)}
          >
            {tenants.map(t => (
              <option key={t.id} value={t.id}>{t.name} ({t.slug})</option>
            ))}
          </select>
        )}
        <div className="doc-mode-toggle">
          <button
            className={`doc-mode-btn${dateMode === 'day'   ? ' doc-mode-btn--active' : ''}`}
            onClick={() => setDateMode('day')}
          >Tag</button>
          <button
            className={`doc-mode-btn${dateMode === 'range' ? ' doc-mode-btn--active' : ''}`}
            onClick={() => setDateMode('range')}
          >Zeitraum</button>
          <button
            className={`doc-mode-btn${dateMode === 'all'   ? ' doc-mode-btn--active' : ''}`}
            onClick={() => setDateMode('all')}
          >Alle</button>
        </div>

        {dateMode === 'day' && (
          <input
            type="date"
            className="form-control doc-filter-field"
            value={date}
            onChange={e => setDate(e.target.value)}
          />
        )}

        {dateMode === 'range' && (
          <>
            <input
              type="date"
              className="form-control doc-filter-field"
              value={dateFrom}
              max={dateTo || undefined}
              onChange={e => setDateFrom(e.target.value)}
            />
            <span className="doc-range-sep">–</span>
            <input
              type="date"
              className="form-control doc-filter-field"
              value={dateTo}
              min={dateFrom || undefined}
              onChange={e => setDateTo(e.target.value)}
            />
          </>
        )}
        <input
          type="text"
          className="form-control doc-filter-search"
          placeholder="Name, Firma, Typ…"
          value={filterText}
          onChange={e => setFilterText(e.target.value)}
        />
      </div>

      {/* Table */}
      <div className="card">
        {loading ? (
          <div className="loading-state"><span className="spinner" /> Laden…</div>
        ) : baseRows.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📄</div>
            <p>{allRows.length === 0 ? 'Keine Besucher im gewählten Zeitraum.' : 'Keine Dokumente im gewählten Zeitraum.'}</p>
          </div>
        ) : rows.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">🔍</div>
            <p>Keine Einträge entsprechen dem Filter.</p>
          </div>
        ) : (
          <>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  {[
                    { key: 'visit_date', label: 'Datum' },
                    { key: 'name',       label: 'Name' },
                    { key: 'company',    label: 'Firma' },
                    { key: 'type',       label: 'Typ' },
                  ].map(col => (
                    <th key={col.key} className="th-sortable" onClick={() => handleSort(col.key)}>
                      {col.label}
                      <span className={`sort-icon${sortKey === col.key ? ' sort-icon--active' : ''}`}>
                        {sortKey === col.key ? (sortDir === 'asc' ? '↑' : '↓') : '↕'}
                      </span>
                    </th>
                  ))}
                  <th>Dokument</th>
                  <th>PDF</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(r => (
                  <tr key={r.session_id}>
                    <td>{formatDate(r.visit_date)}</td>
                    <td>{r.first_name} {r.last_name}</td>
                    <td>{r.company || '—'}</td>
                    <td>{r.flow_mode || '—'}</td>
                    <td>
                      {r.document_available ? (
                        <button
                          className="btn btn-ghost doc-btn"
                          onClick={() => openFile(r.session_id, 'image')}
                          disabled={lbLoading === r.session_id + 'image'}
                        >
                          {lbLoading === r.session_id + 'image' ? '…' : 'Anzeigen'}
                        </button>
                      ) : '—'}
                    </td>
                    <td>
                      {r.pdf_available ? (
                        <button
                          className="btn btn-ghost doc-btn"
                          onClick={() => openFile(r.session_id, 'pdf', `${r.first_name} ${r.last_name}`, r.visit_date)}
                          disabled={lbLoading === r.session_id + 'pdf'}
                        >
                          {lbLoading === r.session_id + 'pdf' ? '…' : '⬇ Herunterladen'}
                        </button>
                      ) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {baseRows.length !== rows.length && (
            <p className="doc-filter-count">{rows.length} von {baseRows.length} Dokumenten</p>
          )}
          </>
        )}
      </div>

      {/* Image lightbox */}
      {lightbox && (
        <div className="doc-lightbox-overlay" onClick={closeLightbox}>
          <div className="doc-lightbox" onClick={e => e.stopPropagation()}>
            <button className="doc-lightbox-close" onClick={closeLightbox}>✕</button>
            <img src={lightbox.url} alt="Dokument" className="doc-lightbox-img" />
            <a
              href={lightbox.url}
              download="dokument.jpg"
              className="btn btn-ghost doc-lightbox-dl"
            >
              Herunterladen
            </a>
          </div>
        </div>
      )}
    </div>
  )
}
